import { resolveApiUrl } from "~/utils/url/urlResolver";
import type { PaginationDTO, SearchProductDataDTO, SortingOptionDTO } from "~/features/search/models/SearchResultsDTO";
import { type SortDirection } from "~/features/search/models/models";
import { getBESortCode } from "~/features/search/redux/searchSelectors";

const API_URL = resolveApiUrl("/api/fcc/search");

export interface FCCSearchResultsDTO {
  results: SearchProductDataDTO[];
  pagination: PaginationDTO;
  sorts: SortingOptionDTO[];
  categoryPath: FCCTreeNodeDTO[];
  productHierarchy: FCCTreeNodeDTO[];
  plantHierarchy: FCCTreeNodeDTO[];
}

export interface FCCTreeNodeDTO {
  name: string;
  code: string;
  count: number;
  children: FCCTreeNodeDTO[];
}

interface SearchParams {
  query?: string;
  categoryPath?: string;
  productHierarchy?: string;
  plantHierarchy?: string;
  page?: number;
  relevantProducts?: boolean;
  sortCode?: string;
  sortDirection?: SortDirection;
}

const FACETS: (keyof SearchParams)[] = ["categoryPath", "productHierarchy", "plantHierarchy"];

export async function fetchFCCSearchResults(
  data: SearchParams,
  abortSignal?: AbortSignal,
): Promise<FCCSearchResultsDTO> {
  const params = new URLSearchParams();
  if (data.query) {
    params.set("text", data.query);
  }
  if (data.page) {
    params.set("page", data.page.toString());
  }
  if (data.relevantProducts) {
    params.set("relevantProducts", "true");
  }
  const qParts: string[] = [];
  qParts.push(data.query ?? ""); // Search string

  qParts.push(getBESortCode(data.sortCode ?? null, data.sortDirection ?? null) ?? "null"); // Sorting
  FACETS.forEach((facet) => {
    if (data[facet]) {
      qParts.push(`${facet}:${data[facet]}`);
    }
  });
  params.set("q", qParts.join(":"));

  const response = await fetch(API_URL + "?" + params.toString(), {
    credentials: "same-origin", // include, omit
    mode: "cors", // no-cors, same-origin
    referrer: "no-referrer", // client
    signal: abortSignal,
  });

  if (response.redirected) {
    window.location.reload();
  }

  if (!response.ok) {
    throw new Error("Error fetching search results");
  }

  return response.json();
}
